import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './MyBooks.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const STATUS_TABS = [
  { id: 'Reading', emoji: '📖', key: 'reading' },
  { id: 'WantToRead', emoji: '📋', key: 'wantToRead' },
  { id: 'Finished', emoji: '✅', key: 'finished' },
  { id: 'Abandoned', emoji: '🚫', key: 'abandoned' },
  { id: 'Owned', emoji: '📚', key: 'myShelf' },
];

function StarRating({ value, onChange, label }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="my-books-star-rating" aria-label={label}>
      {[1, 2, 3, 4, 5].map(star => (
        <span
          key={star}
          className={`my-books-star ${star <= (hover || value) ? 'filled' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          role="button"
          aria-label={`${star} star${star > 1 ? 's' : ''}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function DifficultyRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="my-books-difficulty-rating">
      {[1, 2, 3, 4, 5].map(level => (
        <span
          key={level}
          className={`my-books-difficulty-dot ${level <= (hover || value) ? 'filled' : ''}`}
          onClick={() => onChange(level)}
          onMouseEnter={() => setHover(level)}
          onMouseLeave={() => setHover(0)}
          role="button"
          aria-label={`Difficulty ${level}`}
        >
          ●
        </span>
      ))}
    </div>
  );
}

function MyBooks({ factFictionMode, navigateTo }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('Reading');
  const [books, setBooks] = useState({
    Reading: [],
    WantToRead: [],
    Finished: [],
    Abandoned: [],
    Owned: [],
  });
  const [expandedId, setExpandedId] = useState(null);
  const [editReview, setEditReview] = useState({ bookId: null, review: '', rating: 0, difficultyRating: 0 });
  const [saving, setSaving] = useState(null);

  const fetchAllStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const results = {};
      const statusTabs = STATUS_TABS.filter(t => t.id !== 'Owned');
      await Promise.all(
        statusTabs.map(async (tab) => {
          const res = await apiFetch(instance, `/api/userbookstatus?status=${tab.id}`);
          results[tab.id] = res.ok ? unwrap(await res.json()) : [];
        })
      );
      // Fetch library/owned books separately
      try {
        const libRes = await apiFetch(instance, '/api/userlibrary');
        if (libRes.ok) {
          const libData = unwrap(await libRes.json());
          results.Owned = libData;
        } else {
          results.Owned = [];
        }
      } catch {
        results.Owned = [];
      }
      setBooks(results);
    } catch (e) {
      console.error('Failed to load book statuses:', e);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    fetchAllStatuses();
  }, [fetchAllStatuses]);

  const updateStatus = useCallback(async (bookId, body) => {
    try {
      setSaving(bookId);
      await apiFetchJson(instance, `/api/userbookstatus/${bookId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      await fetchAllStatuses();
    } catch (e) {
      console.error('Failed to update status:', e);
    } finally {
      setSaving(null);
    }
  }, [instance, fetchAllStatuses]);

  const removeStatus = useCallback(async (bookId) => {
    try {
      setSaving(bookId);
      await apiFetchJson(instance, `/api/userbookstatus/${bookId}`, { method: 'DELETE' });
      await fetchAllStatuses();
    } catch (e) {
      console.error('Failed to remove status:', e);
    } finally {
      setSaving(null);
    }
  }, [instance, fetchAllStatuses]);

  const handleStartReading = (bookId) => updateStatus(bookId, { status: 'Reading' });
  const handleMarkFinished = (bookId) => updateStatus(bookId, { status: 'Finished' });
  const handleMarkAbandoned = (bookId) => updateStatus(bookId, { status: 'Abandoned' });

  const createBookStatus = useCallback(async (bookId, status) => {
    try {
      setSaving(bookId);
      const res = await apiFetch(instance, '/api/userbookstatus', {
        method: 'POST',
        body: JSON.stringify({ bookId, status }),
      });
      if (res.ok || res.status === 409) {
        if (res.status === 409) {
          await apiFetch(instance, `/api/userbookstatus/${bookId}`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
          });
        }
        await fetchAllStatuses();
      }
    } catch (e) {
      console.error('Failed to set book status:', e);
    } finally {
      setSaving(null);
    }
  }, [instance, fetchAllStatuses]);

  const handleRating = (bookId, rating) => updateStatus(bookId, { status: 'Finished', rating });
  const handleDifficultyRating = (bookId, difficultyRating) => updateStatus(bookId, { status: 'Finished', difficultyRating });

  const openReviewEditor = (entry) => {
    setEditReview({
      bookId: entry.bookId,
      review: entry.review || '',
      rating: entry.rating || 0,
      difficultyRating: entry.difficultyRating || 0,
    });
  };

  const saveReview = async () => {
    if (!editReview.bookId) return;
    await updateStatus(editReview.bookId, {
      status: 'Finished',
      rating: editReview.rating,
      difficultyRating: editReview.difficultyRating,
      review: editReview.review,
    });
    setEditReview({ bookId: null, review: '', rating: 0, difficultyRating: 0 });
  };

  const closeReviewEditor = () => {
    setEditReview({ bookId: null, review: '', rating: 0, difficultyRating: 0 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const currentBooks = books[activeTab] || [];
  const totalReading = books.Reading.length;
  const totalWantToRead = books.WantToRead.length;
  const totalFinished = books.Finished.length;
  const totalAbandoned = books.Abandoned.length;
  const totalOwned = books.Owned.length;

  const renderCover = (book) => (
    <BookCover src={book?.coverImageUrl} alt={book?.title} className="my-books-cover" emoji="📕" />
  );

  const renderReadingTab = () => (
    <div className="my-books-grid">
      {currentBooks.map(entry => (
        <div key={entry.id} className="my-books-card">
          {renderCover(entry.book)}
          <div className="my-books-card-info">
            <h4 className="my-books-card-title" title={entry.book?.title}>{entry.book?.title}</h4>
            <p className="my-books-card-author">{entry.book?.author}</p>
            {entry.startDate && (
              <p className="my-books-card-date">{t('bookClub.myBooks.started', { date: formatDate(entry.startDate) })}</p>
            )}
            <div className="my-books-card-actions">
              <button
                className="my-books-btn my-books-btn-primary"
                onClick={() => handleMarkFinished(entry.bookId)}
                disabled={saving === entry.bookId}
              >
                ✅ {t('bookClub.myBooks.markFinished')}
              </button>
              <button
                className="my-books-btn my-books-btn-danger"
                onClick={() => handleMarkAbandoned(entry.bookId)}
                disabled={saving === entry.bookId}
              >
                🚫 {t('bookClub.myBooks.abandon')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderWantToReadTab = () => (
    <div className="my-books-grid">
      {currentBooks.map(entry => (
        <div key={entry.id} className="my-books-card">
          {renderCover(entry.book)}
          <div className="my-books-card-info">
            <h4 className="my-books-card-title" title={entry.book?.title}>{entry.book?.title}</h4>
            <p className="my-books-card-author">{entry.book?.author}</p>
            {entry.book?.pageCount > 0 && (
              <p className="my-books-card-meta">{entry.book.pageCount} {t('bookClub.common.pages')}</p>
            )}
            <div className="my-books-card-actions">
              <button
                className="my-books-btn my-books-btn-primary"
                onClick={() => handleStartReading(entry.bookId)}
                disabled={saving === entry.bookId}
              >
                📖 {t('bookClub.myBooks.startReading')}
              </button>
              <button
                className="my-books-btn my-books-btn-muted"
                onClick={() => removeStatus(entry.bookId)}
                disabled={saving === entry.bookId}
              >
                ✕ {t('bookClub.common.remove')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderFinishedTab = () => (
    <div className="my-books-grid my-books-grid-finished">
      {currentBooks.map(entry => {
        const isExpanded = expandedId === entry.id;
        return (
          <div
            key={entry.id}
            className={`my-books-card my-books-card-finished ${isExpanded ? 'expanded' : ''}`}
          >
            {renderCover(entry.book)}
            <div className="my-books-card-info">
              <h4 className="my-books-card-title" title={entry.book?.title}>{entry.book?.title}</h4>
              <p className="my-books-card-author">{entry.book?.author}</p>

              <div className="my-books-rating-row">
                <span className="my-books-rating-label">{t('bookClub.myBooks.yourRating')}</span>
                <StarRating
                  value={entry.rating || 0}
                  onChange={(r) => handleRating(entry.bookId, r)}
                  label="Book rating"
                />
                {entry.book?.averageRating > 0 && (
                  <span className="my-books-community-avg" title={`${entry.book.ratingCount} community ratings`}>
                    Community: ⭐ {entry.book.averageRating}
                  </span>
                )}
              </div>

              <div className="my-books-rating-row">
                <span className="my-books-rating-label">{t('bookClub.myBooks.difficulty')}</span>
                <DifficultyRating
                  value={entry.difficultyRating || 0}
                  onChange={(d) => handleDifficultyRating(entry.bookId, d)}
                />
                {entry.book?.difficultyScore > 0 && (
                  <span className="my-books-community-avg" title={`${entry.book.difficultyRatingCount} community ratings`}>
                    Avg: {entry.book.difficultyScore}/5
                  </span>
                )}
              </div>

              {entry.review && !isExpanded && (
                <p className="my-books-review-snippet">
                  {entry.review.length > 80 ? entry.review.slice(0, 80) + '…' : entry.review}
                </p>
              )}

              <div
                className="my-books-expand-toggle"
                onClick={() => setExpandedId(isExpanded ? null : entry.id)}
              >
                {isExpanded ? '▲ ' + t('bookClub.myBooks.less') : '▼ ' + t('bookClub.myBooks.more')}
              </div>

              {isExpanded && (
                <div className="my-books-expanded-body">
                  {entry.finishDate && (
                    <p className="my-books-card-date">{t('bookClub.myBooks.finishedDate', { date: formatDate(entry.finishDate) })}</p>
                  )}
                  {entry.review && (
                    <p className="my-books-review-full">{entry.review}</p>
                  )}
                  <button
                    className="my-books-btn my-books-btn-secondary"
                    onClick={() => openReviewEditor(entry)}
                  >
                    ✏️ {entry.review ? t('bookClub.myBooks.editReview') : t('bookClub.myBooks.writeReview')}
                  </button>
                  {navigateTo && <button className="bookclub-nav-link" onClick={() => navigateTo('journal')}>📓 {t('bookClub.myBooks.writeJournalEntry')}</button>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderAbandonedTab = () => (
    <div className="my-books-grid">
      {currentBooks.map(entry => (
        <div key={entry.id} className="my-books-card my-books-card-abandoned">
          {renderCover(entry.book)}
          <div className="my-books-card-info">
            <h4 className="my-books-card-title" title={entry.book?.title}>{entry.book?.title}</h4>
            <p className="my-books-card-author">{entry.book?.author}</p>
            <div className="my-books-card-actions">
              <button
                className="my-books-btn my-books-btn-primary"
                onClick={() => handleStartReading(entry.bookId)}
                disabled={saving === entry.bookId}
              >
                📖 {t('bookClub.myBooks.retry')}
              </button>
              <button
                className="my-books-btn my-books-btn-muted"
                onClick={() => removeStatus(entry.bookId)}
                disabled={saving === entry.bookId}
              >
                ✕ {t('bookClub.common.remove')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const OWNERSHIP_LABELS = { Owned: '📗 ' + t('bookClub.myBooks.ownedLabel'), Digital: '📱 ' + t('bookClub.myBooks.digitalLabel'), Borrowed: '🔄 ' + t('bookClub.myBooks.borrowedLabel'), Wishlist: '💫 ' + t('bookClub.myBooks.wishlistLabel') };

  const renderOwnedTab = () => (
    <div className="my-books-grid">
      {currentBooks.map(entry => {
        const bookId = entry.book?.id || entry.bookId;
        return (
          <div key={entry.id} className="my-books-card">
            {renderCover(entry.book)}
            <div className="my-books-card-info">
              <h4 className="my-books-card-title" title={entry.book?.title}>{entry.book?.title}</h4>
              <p className="my-books-card-author">{entry.book?.author}</p>
              <span className="my-books-ownership-badge">
                {OWNERSHIP_LABELS[entry.ownership] || entry.ownership}
              </span>
              {entry.book?.pageCount > 0 && (
                <p className="my-books-card-meta">{entry.book.pageCount} {t('bookClub.common.pages')}</p>
              )}
              <div className="my-books-card-actions">
                <button
                  className="my-books-btn my-books-btn-primary"
                  onClick={() => createBookStatus(bookId, 'Reading')}
                  disabled={saving === bookId}
                >
                  📖 {t('bookClub.myBooks.startReading')}
                </button>
                <button
                  className="my-books-btn my-books-btn-secondary"
                  onClick={() => createBookStatus(bookId, 'Finished')}
                  disabled={saving === bookId}
                >
                  ✅ {t('bookClub.myBooks.markAsRead')}
                </button>
                <button
                  className="my-books-btn my-books-btn-muted"
                  onClick={() => createBookStatus(bookId, 'WantToRead')}
                  disabled={saving === bookId}
                >
                  📌 {t('bookClub.myBooks.tbr')}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="my-books-empty">
          <span className="my-books-empty-icon">⏳</span>
          <p>{t('bookClub.myBooks.loading')}</p>
        </div>
      );
    }
    if (currentBooks.length === 0) {
      const tab = STATUS_TABS.find(t => t.id === activeTab);
      return (
        <div className="my-books-empty">
          <span className="my-books-empty-icon">{tab?.emoji}</span>
          <p>{t('bookClub.myBooks.noBooks', { tab: t('bookClub.myBooks.' + tab?.key) })}</p>
          {navigateTo && <button className="bookclub-nav-link" onClick={() => navigateTo('search')}>🔎 {t('bookClub.common.findBooks')}</button>}
        </div>
      );
    }
    switch (activeTab) {
      case 'Reading': return renderReadingTab();
      case 'WantToRead': return renderWantToReadTab();
      case 'Finished': return renderFinishedTab();
      case 'Abandoned': return renderAbandonedTab();
      case 'Owned': return renderOwnedTab();
      default: return null;
    }
  };

  return (
    <div className="my-books-container">
      <div className="my-books-stats-bar">
        <div className="my-books-stat">
          <span className="my-books-stat-count">{totalReading}</span>
          <span className="my-books-stat-label">{t('bookClub.myBooks.reading')}</span>
        </div>
        <div className="my-books-stat">
          <span className="my-books-stat-count">{totalWantToRead}</span>
          <span className="my-books-stat-label">{t('bookClub.myBooks.wantToRead')}</span>
        </div>
        <div className="my-books-stat">
          <span className="my-books-stat-count">{totalFinished}</span>
          <span className="my-books-stat-label">{t('bookClub.myBooks.finished')}</span>
        </div>
        <div className="my-books-stat">
          <span className="my-books-stat-count">{totalAbandoned}</span>
          <span className="my-books-stat-label">{t('bookClub.myBooks.abandoned')}</span>
        </div>
        <div className="my-books-stat">
          <span className="my-books-stat-count">{totalOwned}</span>
          <span className="my-books-stat-label">{t('bookClub.myBooks.myShelf')}</span>
        </div>
      </div>

      <div className="my-books-tabs">
        {STATUS_TABS.map(tab => {
          const count = books[tab.id]?.length || 0;
          return (
            <button
              key={tab.id}
              className={`my-books-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.emoji} {t('bookClub.myBooks.' + tab.key)} <span className="my-books-tab-count">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="my-books-content">
        {renderTabContent()}
      </div>

      {editReview.bookId && (
        <div className="my-books-modal-overlay" onClick={closeReviewEditor}>
          <div className="my-books-modal" onClick={(e) => e.stopPropagation()}>
            <div className="my-books-modal-header">
              <h3>✏️ {t('bookClub.myBooks.writeReview')}</h3>
              <button className="my-books-modal-close" onClick={closeReviewEditor}>✕</button>
            </div>
            <div className="my-books-modal-body">
              <div className="my-books-rating-row">
                <span className="my-books-rating-label">{t('bookClub.myBooks.rating')}</span>
                <StarRating
                  value={editReview.rating}
                  onChange={(r) => setEditReview(prev => ({ ...prev, rating: r }))}
                  label="Edit rating"
                />
              </div>
              <div className="my-books-rating-row">
                <span className="my-books-rating-label">{t('bookClub.myBooks.difficulty')}</span>
                <DifficultyRating
                  value={editReview.difficultyRating}
                  onChange={(d) => setEditReview(prev => ({ ...prev, difficultyRating: d }))}
                />
              </div>
              <textarea
                className="my-books-review-textarea"
                placeholder={t('bookClub.myBooks.reviewPlaceholder')}
                value={editReview.review}
                onChange={(e) => setEditReview(prev => ({ ...prev, review: e.target.value }))}
                rows={5}
              />
            </div>
            <div className="my-books-modal-footer">
              <button className="my-books-btn my-books-btn-muted" onClick={closeReviewEditor}>{t('bookClub.common.cancel')}</button>
              <button
                className="my-books-btn my-books-btn-primary"
                onClick={saveReview}
                disabled={saving}
              >
                💾 {t('bookClub.myBooks.saveReview')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyBooks;
